// components/share/ShareSheetParts.tsx — the share sheet's shared CHROME.
//
// 1.24.00 E1 gave the profile dialog the post dialog's bar. The model (the six
// platform hrefs, the ≤280 note rules) lives in core/lib/shareTargets.ts; this
// file is the other half — the tile grid, the disc tones, and the optional-note
// field — so the two sheets can never drift into looking like two products.
//
// Lifted verbatim out of SharePostDialog.tsx (1.23.90); the DOM each part emits
// is unchanged, which is why that dialog's tests did not move.

import { useId } from 'react';
import type { ChangeEvent } from 'react';
import styled, { css } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import { Textarea } from '@/ui';
import { Icon } from '@/icons';
import type { IconName } from '@/icons';
import {
  SHARE_MESSAGE_MAX,
  shareCounterAnnouncement,
  type ShareTarget,
  type Tone,
} from '@/core/lib/shareTargets';
import { ShareSrOnly } from './shareChrome';

// ─── Tile chrome ──────────────────────────────────────────────────────────────

const tileStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px 4px 8px;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radius.md};
  background: transparent;
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  text-decoration: none;
  cursor: pointer;
  transition:
    background 0.12s,
    border-color 0.12s,
    transform 80ms ease;

  &:hover {
    background: ${({ theme }) => theme.color.hover};
  }

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const TileButton = styled.button`
  ${tileStyle}
`;

const TileLink = styled.a`
  ${tileStyle}
`;

const TileLabel = styled.span`
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Disc + ink per tone. Brand hexes are the platforms' own marks; white ink on
 *  each is ≥3:1 (non-text contrast) in either theme because the disc itself is
 *  the background, not the page. Theme tones use tokens so X flips black/white
 *  with the theme exactly as X's own mark does. */
function toneStyle(tone: Tone, theme: DefaultTheme): { background: string; color: string } {
  switch (tone) {
    case 'x':
      return { background: theme.color.textPrimary, color: theme.color.bgRoot };
    case 'accent':
      return { background: theme.color.accentCyan, color: theme.color.onAccent };
    case 'whatsapp':
      return { background: '#128C7E', color: '#FFFFFF' };
    case 'telegram':
      return { background: '#0088CC', color: '#FFFFFF' };
    case 'facebook':
      return { background: '#1877F2', color: '#FFFFFF' };
    case 'reddit':
      return { background: '#FF4500', color: '#FFFFFF' };
    case 'neutral':
    default:
      return { background: theme.color.surfaceRaised, color: theme.color.textPrimary };
  }
}

const Disc = styled.span<{ $tone: Tone }>`
  display: inline-grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  ${({ $tone, theme }) => {
    const { background, color } = toneStyle($tone, theme);
    return css`
      background: ${background};
      color: ${color};
    `;
  }}
`;

// ─── Tiles ────────────────────────────────────────────────────────────────────

export interface ShareTileActionProps {
  icon: IconName;
  label: string;
  onClick: () => void;
}

/** An in-app tile (Copy link, the device's own share sheet). Always a real
 *  control — nothing in this grid is ever wired to nothing. */
export function ShareTileAction({ icon, label, onClick }: ShareTileActionProps) {
  return (
    <TileButton type="button" onClick={onClick}>
      <Disc $tone="neutral">
        <Icon name={icon} size={20} />
      </Disc>
      <TileLabel>{label}</TileLabel>
    </TileButton>
  );
}

/** One platform anchor built by `buildPlatformTargets`. */
export function SharePlatformTile({ target }: { target: ShareTarget }) {
  return (
    <TileLink
      href={target.href}
      aria-label={target.ariaLabel}
      data-share-target={target.key}
      {...(target.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      <Disc $tone={target.tone}>
        <Icon name={target.icon} size={20} />
      </Disc>
      <TileLabel>{target.label}</TileLabel>
    </TileLink>
  );
}

// ─── Optional note ────────────────────────────────────────────────────────────

const Counter = styled.p`
  margin: 4px 0 0;
  text-align: right;
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

export interface ShareMessageFieldProps {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
}

/**
 * The optional ≤280-char note. It rides in every platform href and in the
 * native share payload — never in Copy link, which copies the URL alone.
 */
export function ShareMessageField({ value, onChange, placeholder }: ShareMessageFieldProps) {
  const hintId = useId();
  const counterId = useId();

  function handleChange(e: ChangeEvent<HTMLTextAreaElement>) {
    // maxLength on the element covers typing; the slice covers paste/IME/programmatic
    // sets that jsdom and some browsers let through past the attribute.
    onChange(e.target.value.slice(0, SHARE_MESSAGE_MAX));
  }

  return (
    <div>
      <Textarea
        label="Add a message (optional)"
        value={value}
        onChange={handleChange}
        maxLength={SHARE_MESSAGE_MAX}
        rows={2}
        placeholder={placeholder}
        aria-describedby={`${hintId} ${counterId}`}
      />
      {/* Static hint + the visible counter are the textarea's DESCRIPTION
          (read once on focus). Neither is a live region — the polite
          announcer below is, and it only changes text at milestones. */}
      <ShareSrOnly id={hintId}>Up to {SHARE_MESSAGE_MAX} characters.</ShareSrOnly>
      <Counter id={counterId}>
        {value.length} / {SHARE_MESSAGE_MAX}
      </Counter>
      <ShareSrOnly aria-live="polite" aria-atomic="true" data-testid="share-message-announcer">
        {shareCounterAnnouncement(value.length)}
      </ShareSrOnly>
    </div>
  );
}
